/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { combineLatest, Observable, Subject, filter, map, takeUntil, tap, withLatestFrom } from "rxjs";
import { CatchmentDto, DataPointDto, StationDataDto, StationDto, StationSimulationDataDto } from "src/app/services/api";
import { RootState } from "src/app/store";
import * as WatershedActions from "src/app/store/watershed/actions";
import * as FromWatershed from "src/app/store/watershed/selectors";
import * as FromApp from "src/app/store/app/selectors";
import { TranslateService } from "@ngx-translate/core";
import { WIND_SPEED_ALERT_LEVELS } from "src/app/constants";
import { WIND_SPEED_LEVELS } from "src/app/constants";
import { WindSpeedAlertLevels } from "src/app/interfaces";

@Component({
  selector: "app-station",
  templateUrl: "./station.component.html",
  styleUrls: ["./station.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StationComponent {
  public readonly stationCode$: Observable<string | null> = this.store.select(FromWatershed.selectStationCode);

  public readonly station$: Observable<StationDto | undefined> = this.store.select(FromWatershed.selectStation);
  public readonly stationData$: Observable<StationDataDto | undefined> = this.store.select(FromWatershed.selectStationData);

  public readonly stationSimulationWaterLevelData$: Observable<DataPointDto[]> = this.store.select(
    FromWatershed.selectStationSimulationWaterLevelData,
  );

  public readonly stationSimulationDischargeData$: Observable<DataPointDto[]> = this.store.select(
    FromWatershed.selectStationSimulationDischargeData,
  );

  public readonly catchment$: Observable<CatchmentDto | undefined> = this.store.select(FromWatershed.selectWatershedCatchment);

  public readonly hasTemperatureData$: Observable<boolean> = this.store.select(FromWatershed.selectHasStationDataTemperature);
  public readonly hasThermalSensationData$: Observable<boolean> = this.store.select(FromWatershed.selectHasStationDataThermalSensation);
  public readonly hasWindData$: Observable<boolean> = this.store.select(FromWatershed.selectHasStationDataWind);
  public readonly hasWindGustData$: Observable<boolean> = this.store.select(FromWatershed.selectHasStationDataWindGust);
  public readonly hasHumidityData$: Observable<boolean> = this.store.select(FromWatershed.selectHasStationDataHumidity);
  public readonly hasAirHumidityData$: Observable<boolean> = this.store.select(FromWatershed.selectHasStationDataAirHumidity);
  public readonly hasWindDirectionData$: Observable<boolean> = this.store.select(FromWatershed.selectHasStationDataWindDirection);

  public readonly emptyTemperatureData$: Observable<boolean> = this.store.select(FromWatershed.selectEmptyStationDataTemperature);
  public readonly emptyThermalSensationData$: Observable<boolean> = this.store.select(FromWatershed.selectEmptyStationDataThermalSensation);
  public readonly emptyWindData$: Observable<boolean> = this.store.select(FromWatershed.selectEmptyStationDataWind);
  public readonly emptyHumidityData$: Observable<boolean> = this.store.select(FromWatershed.selectEmptyStationDataHumidity);

  public readonly temperature$: Observable<number | null> = this.store.select(FromWatershed.selectStationDataTemperature);
  public readonly thermalSensation$: Observable<number | null> = this.store.select(FromWatershed.selectStationDataThermalSensation);
  public readonly wind$: Observable<number | null> = this.store.select(FromWatershed.selectStationDataWind);
  public readonly windGust$: Observable<number | null> = this.store.select(FromWatershed.selectStationDataWindGust);
  public readonly windDirection$: Observable<number | null> = this.store.select(FromWatershed.selectStationDataWindDirection);
  public readonly humidity$: Observable<number | null> = this.store.select(FromWatershed.selectStationDataHumidity);

  public readonly temperatureHours$: Observable<DataPointDto[]> = this.store.select(FromWatershed.selectStationDataTemperatureHours);
  public readonly thermalSensationHours$: Observable<DataPointDto[]> = this.store.select(
    FromWatershed.selectStationDataThermalSensationHours,
  );

  public readonly windHours$: Observable<DataPointDto[]> = this.store.select(FromWatershed.selectStationDataWindHours);
  public readonly windGustHours$: Observable<DataPointDto[]> = this.store.select(FromWatershed.selectStationDataWindGustHours);
  public readonly windDirectionHours$: Observable<DataPointDto[]> = this.store.select(FromWatershed.selectStationDataWindDirectionHours);
  public readonly humidityHours$: Observable<DataPointDto[]> = this.store.select(FromWatershed.selectStationDataHumidityHours);

  public readonly refreshState$: Observable<boolean> = this.store.select(FromApp.selectRefreshState);

  // create a observable for selectMaxStationPrecipitationData
  public readonly stationPrecipitation10Data$: Observable<DataPointDto[]> = this.store.select(
    FromWatershed.selectStationtPrecipitation10Data,
  );

  public readonly stationPrecipitation60Data$: Observable<DataPointDto[]> = this.store.select(
    FromWatershed.selectStationtPrecipitation60Data,
  );

  public readonly hasPrecipitation10Data$: Observable<boolean> = this.stationPrecipitation10Data$.pipe(map(data => !!data?.length));
  public readonly hasPrecipitation60Data$: Observable<boolean> = this.stationPrecipitation60Data$.pipe(map(data => !!data?.length));

  public readonly emptyPrecipitationData$: Observable<boolean> = combineLatest([
    this.hasPrecipitation10Data$,
    this.hasPrecipitation60Data$,
  ]).pipe(map(([has10, has60]) => !has10 && !has60));

  public readonly hasWaterLevelData$: Observable<boolean> = this.stationData$.pipe(map(data => !!data?.waterLevel?.length));
  public readonly hasDischargeData$: Observable<boolean> = this.stationData$.pipe(map(data => !!data?.discharge?.length));

  public readonly emptyHidroData$: Observable<boolean> = combineLatest([this.hasWaterLevelData$, this.hasDischargeData$]).pipe(
    map(([hasWL, hasDischarge]) => !hasWL && !hasDischarge),
  );

  public readonly WIND_SPEED_LEVELS = WIND_SPEED_LEVELS;
  public readonly windSpeedAlertLevels: WindSpeedAlertLevels = WIND_SPEED_ALERT_LEVELS;

  public readonly catchmentPrecipitatio10nData$: Observable<DataPointDto[]> = this.store.select(
    FromWatershed.selectCatchmentPrecipitation10Data,
  );

  public readonly catchmentPrecipitation60Data$: Observable<DataPointDto[]> = this.store.select(
    FromWatershed.selectCatchmentPrecipitation60Data,
  );

  private unsubscribeSubject$!: Subject<void>;

  constructor(
    private readonly store: Store<RootState>,
    public readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  public ionViewWillEnter(): void {
    this.unsubscribeSubject$ = new Subject();

    this.stationCode$
      .pipe(
        takeUntil(this.unsubscribeSubject$),
        filter((stationCode): stationCode is string => !!stationCode),
        tap(stationCode => {
          this.store.dispatch(WatershedActions.loadStationData({ stationCode }));
          this.store.dispatch(WatershedActions.loadStationSimulationData({ stationId: stationCode }));
        }),
      )
      .subscribe();

    this.refreshState$
      .pipe(
        takeUntil(this.unsubscribeSubject$),
        filter(refreshState => !!refreshState),
        withLatestFrom(this.stationCode$),
        filter((data): data is [boolean, string] => !!data[1]),
        tap(([, stationCode]) => {
          this.store.dispatch(WatershedActions.loadStationData({ stationCode }));
          this.store.dispatch(WatershedActions.loadStationSimulationData({ stationId: stationCode }));
        }),
      )
      .subscribe();

    // Force change detection when station data changes
    this.stationData$
      .pipe(
        takeUntil(this.unsubscribeSubject$),
        filter(data => !!data),
        tap(() => {
          // Use setTimeout to ensure the change detection runs after the data is fully propagated
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 0);
        }),
      )
      .subscribe();

    // Force change detection when precipitation data changes
    combineLatest([this.stationPrecipitation10Data$, this.stationPrecipitation60Data$])
      .pipe(
        takeUntil(this.unsubscribeSubject$),
        tap(() => {
          setTimeout(() => {
            this.cdr.detectChanges();
          }, 0);
        }),
      )
      .subscribe();
  }

  public ionViewWillLeave(): void {
    this.unsubscribeSubject$.next();
    this.unsubscribeSubject$.complete();
  }

  public resetToIslandsView(): void {
    this.store.dispatch(WatershedActions.setStationCode({ stationCode: null! }));
    this.store.dispatch(WatershedActions.setCatchmentId({ catchmentId: null! }));
    this.store.dispatch(WatershedActions.setWatershedId({ watershedId: null! }));
    this.store.dispatch(WatershedActions.setIslandId({ islandId: null! }));
    this.store.dispatch(WatershedActions.resetToIslandsView());
  }

  public async closeStation(): Promise<void> {
    this.store.dispatch(WatershedActions.setStationCode({ stationCode: null! }));
  }
}
